import { functionUrl } from "@/lib/functions";
import { PARSEABLE_DOC_MIME_TYPES } from "@shared/mime-types";
import { updateAttachmentRow } from "./api/attachments-api";
import type { LlmAttachmentInput, ParsedAttachmentContent } from "./attachments.types";

export function sanitizeFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf(".");
  const name = lastDot >= 0 ? fileName.slice(0, lastDot) : fileName;
  const ext = lastDot >= 0 ? fileName.slice(lastDot) : "";

  const sanitizedName =
    name
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9._-]/g, "")
      .replace(/_{2,}/g, "_")
      .replace(/^[._-]+/, "") || "file";

  const sanitizedExt = ext.replace(/[^a-zA-Z0-9.]/g, "");

  return sanitizedName + sanitizedExt;
}

export function buildAttachmentPath({
  containerId,
  messageId,
  fileName,
  now = Date.now(),
}: {
  containerId: string;
  messageId: string;
  fileName: string;
  now?: number;
}): string {
  return `${containerId}/${messageId}/${now}-${sanitizeFileName(fileName)}`;
}

export async function buildLangchainMessageContent({
  content,
  attachments,
  authToken,
}: {
  content: string;
  attachments: LlmAttachmentInput[];
  authToken?: string;
}) {
  const contentItems: any[] = content.trim() ? [{ type: "text", text: content.trim() }] : [];

  const attachmentItems = await Promise.all(
    attachments.map(async (attachment): Promise<any[]> => {
      if (attachment.mimeType?.startsWith("image/")) {
        return [{ type: "image_url", image_url: { url: attachment.signedUrl } }];
      }

      if (
        attachment.mimeType &&
        (attachment.mimeType.startsWith("text/") ||
          attachment.mimeType === "application/json" ||
          attachment.mimeType === "application/csv" ||
          attachment.mimeType === "application/x-ipynb+json" ||
          attachment.mimeType === "application/x-bibtex" ||
          attachment.mimeType === "application/x-latex")
      ) {
        const res = await fetch(attachment.signedUrl);
        if (!res.ok) {
          throw new Error(`Failed to fetch attachment content: ${res.status}`);
        }
        return [{ type: "text", text: await res.text() }];
      }

      if (attachment.mimeType && PARSEABLE_DOC_MIME_TYPES.has(attachment.mimeType)) {
        if (attachment.parsedContent) {
          return attachment.parsedContent;
        }

        // Header names are protocol data: never let wuchale translate them.
        // @wc-ignore
        const parseHeaders: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (authToken) {
          // @wc-ignore
          parseHeaders["Authorization"] = authToken;
        }

        const response = await fetch(functionUrl("parse-document"), {
          method: "POST",
          headers: parseHeaders,
          body: JSON.stringify({
            documentUrl: attachment.signedUrl,
          }),
        });

        if (!response.ok) {
          console.error("Document parsing failed with status", response.status);
          throw new Error(`Document parsing failed with status ${response.status}`);
        }

        const json = (await response.json()) as {
          ok: boolean;
          content: ParsedAttachmentContent;
          rawPageCount: number;
          summary?: string | null;
        };
        const parsedContent = json.content;
        await updateAttachmentRow({
          id: attachment.id,
          parsedContent,
          fileSummary: json.summary ?? undefined,
        });
        return parsedContent;
      }

      return [];
    }),
  );

  return [...contentItems, ...attachmentItems.flat()];
}
